<h2 mat-dialog-title>Request a court change</h2>

@if (!submitted) {
  <mat-dialog-content>
    <form [formGroup]="form" class="court-request-form">
      <p class="intro">
        Request a correction to a court or ask us to add one that is missing. Submitting sends this for
        review — the court is not changed right away.
      </p>

      <mat-button-toggle-group
        class="kind-toggle"
        formControlName="request_kind"
        (change)="onKindChange($event.value)"
        aria-label="Request type">
        <mat-button-toggle [value]="Kind.COURT_UPDATE" [disabled]="courts.length === 0">
          Update an existing court
        </mat-button-toggle>
        <mat-button-toggle [value]="Kind.NEW_COURT">Add a new court</mat-button-toggle>
      </mat-button-toggle-group>

      @if (isUpdate) {
        <mat-form-field appearance="outline" class="compact full-width">
          <mat-label>Court to update</mat-label>
          <mat-select formControlName="target_court_id" (selectionChange)="onTargetCourtChange($event.value)">
            @for (court of courts; track court.id) {
              <mat-option [value]="court.id">{{ court.name }}</mat-option>
            }
          </mat-select>
          @if (form.get('target_court_id')?.hasError('required')) {
            <mat-error>Choose the court you want to update</mat-error>
          }
        </mat-form-field>
      }

      <div class="field-grid">
        <mat-form-field appearance="outline" class="compact span-2">
          <mat-label>Court name</mat-label>
          <input matInput formControlName="name" />
          @if (form.get('name')?.hasError('required')) {
            <mat-error>A court name is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>Court type</mat-label>
          <mat-select formControlName="type">
            @for (type of courtTypes; track type) {
              <mat-option [value]="type">{{ type }}</mat-option>
            }
          </mat-select>
          @if (form.get('type')?.hasError('required')) {
            <mat-error>A court type is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact span-2">
          <mat-label>Street address</mat-label>
          <input matInput formControlName="street_address" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>Street address 2</mat-label>
          <input matInput formControlName="street_address_2" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>City</mat-label>
          <input matInput formControlName="city" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>State</mat-label>
          <input matInput formControlName="state" maxlength="2" />
          @if (form.get('state')?.hasError('required')) {
            <mat-error>A state is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>ZIP code</mat-label>
          <input matInput formControlName="zip_code" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>Phone number</mat-label>
          <input matInput formControlName="phone_number" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>Precinct</mat-label>
          <input matInput formControlName="precinct" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>Place</mat-label>
          <input matInput formControlName="place" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="compact">
          <mat-label>District</mat-label>
          <input matInput formControlName="district" />
        </mat-form-field>

        @if (!isUpdate) {
          <mat-form-field appearance="outline" class="compact span-2">
            <mat-label>County or venue</mat-label>
            <input matInput formControlName="county_or_venue" />
            <mat-hint>Helps us link the new court to the right county.</mat-hint>
            @if (form.get('county_or_venue')?.hasError('required')) {
              <mat-error>A county or venue is required</mat-error>
            }
          </mat-form-field>
        }
      </div>

      <mat-form-field appearance="outline" class="compact full-width">
        <mat-label>Source URL</mat-label>
        <input matInput formControlName="source_url" placeholder="https://" />
        <mat-hint>A link to the court website or official source that backs up this request.</mat-hint>
        @if (form.get('source_url')?.hasError('required')) {
          <mat-error>A source URL is required</mat-error>
        }
        @if (form.get('source_url')?.hasError('url')) {
          <mat-error>Enter a full URL starting with http:// or https://</mat-error>
        }
      </mat-form-field>

      @if (isUpdate && !!form.get('target_court_id')?.value && !canSubmit && !submitting) {
        <p class="change-hint">Change at least one field to request an update.</p>
      }

      @if (errorMessage) {
        <p class="error-message" role="alert">{{ errorMessage }}</p>
      }
    </form>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button mat-dialog-close [disabled]="submitting">Cancel</button>
    <button mat-flat-button color="accent" [disabled]="!canSubmit" (click)="submit()">
      @if (submitting) {
        <mat-spinner class="submit-spinner" diameter="18"></mat-spinner>
      } @else {
        Submit for review
      }
    </button>
  </mat-dialog-actions>
} @else {
  <mat-dialog-content>
    <div class="confirmation">
      <mat-icon fontSet="fa" fontIcon="fa-circle-check" class="confirmation-icon"></mat-icon>
      <h3 class="confirmation-title">Request submitted for review</h3>
      <p class="confirmation-body">{{ confirmationMessage }}</p>
    </div>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-flat-button color="accent" (click)="done()">Done</button>
  </mat-dialog-actions>
}
